/** @typedef {typeof import("./base")} BaseUtils */

const EXPERIMENT_FLAGS = {};

const animationEffects = {
  popup: "scale",
  "slide-right": "translateX",
  "slide-down": "translateY",
};
let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, "passive", {
    get: function () {
      supportsPassive = true;
    },
  });
  window.addEventListener("testPassive", null, opts);
  window.removeEventListener("testPassive", null, opts);
} catch (e) {}

// Optimizely Feature Flag
function featureFlag(flagKey, defaultValue = false) {
  const val = EXPERIMENT_FLAGS[flagKey];
  if (val === undefined) {
    return defaultValue;
  }
  return val;
}

function useFeatureFlag(
  flag,
  defaultValue,
  onChange = () => {
    /* fix SonarQube warning */
  },
) {
  const currentValue = featureFlag(flag, defaultValue);
  if (flag) {
    if (currentValue != defaultValue) {
      onChange(currentValue);
    }
    window.removeEventListener(flag, onChange);
    window.addEventListener(flag, () => {
      onChange(featureFlag(flag, defaultValue));
    });
  }
}

/* window.optimizelyFeatureFlags = window.optimizelyFeatureFlags || []
   window.optimizelyFeatureFlags.push({key: '', value: '' })
 */

if (
  window.optimizelyFeatureFlags &&
  Array.isArray(window.optimizelyFeatureFlags)
) {
  window.optimizelyFeatureFlags.forEach((entry) => {
    EXPERIMENT_FLAGS[entry.key] = entry.value;
  });
}
window.optimizelyFeatureFlags = {
  push(entry = {}) {
    EXPERIMENT_FLAGS[entry.key] = entry.value;
    window.dispatchEvent(new Event(entry.key));
  },
};
function isValidEmail(email) {
  const regEx =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regEx.test(String(email).toLowerCase());
}
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

const trackOutOfStockModalShown = (productVariantId) => {
  layerPush({
    event: "outOfStockModalShown",
    outOfStockProductVariantId: productVariantId,
  });
};

const trackCartOpened = () => {
  triggerAlternativeCartCopyExperiment();
  layerPush({
    event: "cartModalOpen",
  });
};

const trackCarouselSwiped = (carouselData) => {
  return layerPush({
    event: "carouselSwiped",
    carouselData,
  });
};

/**Todo: Move this to componnent that needs it */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe",
    ),
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  let elements = getFocusableElements(container);
  let first = elements[0];
  let last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  if (!elementToFocus) return;
  elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (
      event.origin !== undefined &&
      theme.settings &&
      theme.settings.cookieDomain &&
      !event.origin.includes(theme.settings.cookieDomain)
    )
      return;
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    if (
      event.origin !== undefined &&
      theme.settings &&
      theme.settings.cookieDomain &&
      !event.origin.includes(theme.settings.cookieDomain)
    )
      return;
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true,
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slidersScroll = Number(this.dataset.itemsScroll) || 1;
    this.nextButtonId = this.dataset.nextButtonId;
    this.prevButtonId = this.dataset.prevButtonId;
    this.direction = this.dataset.direction || "horizontal";
    this.loopBackToStart = this.dataset.loopBackToStart;
  }
  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.itemsInView = this.querySelector(
      ".featured-collection__products-container",
    )?.dataset?.viewProductsCount;
    if (this.dataset.trackingType !== "pdp-carousel") {
      this.sliderItems = Array.from(
        this.querySelectorAll('[id^="Slide-"]'),
      ).filter((el) => el.closest(this.tagName.toLowerCase()) === this);
    } else {
      this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    }

    this.visibleSlides = this.slider.dataset.sliderCount
      ? this.slider.dataset.sliderCount - 1
      : 0;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");

    this.slidePrevCounter = this.querySelector(".page-counter__prev-button");
    this.slideNextCounter = this.querySelector(".page-counter__next-button");

    if (this.nextButtonId)
      this.nextButton = this.querySelector(this.nextButtonId);
    if (this.prevButtonId)
      this.prevButton = this.querySelector(this.prevButtonId);
    Array.from(this.children).forEach((child) => {
      if (
        !this.prevButton &&
        (child.classList.contains("carousel__button--prev") ||
          child.getAttribute("name") === "previous")
      ) {
        this.prevButton = child;
      } else if (
        !this.nextButton &&
        (child.classList.contains("carousel__button--next") ||
          child.getAttribute("name") === "next")
      ) {
        this.nextButton = child;
      } else if (child.classList.contains("carousel__inner")) {
        this.slider = child;
        this.sliderItems = this.slider.querySelectorAll('[id^="Slide-"]');
      }
    });

    if (!this.slider) return;

    this.offset = this.slider.dataset.offset || 0;
    if (ResizeObserver in window) {
      const resizeObserver = new ResizeObserver(() => this.initPages());
      resizeObserver.observe(this.slider);
    } else {
      this.initPages();
    }
    this.slider.addEventListener(
      "scroll",
      this.update.bind(this),
      supportsPassive ? { passive: true } : false,
    );
    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
    }

    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0,
    );
    if (this.sliderItemsToShow.length < 2) return;
    if (this.direction === "horizontal") {
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetLeft -
        this.sliderItemsToShow[0].offsetLeft;
      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
          this.sliderItemOffset,
      );
    } else {
      this.sliderItemOffset =
        this.sliderItemsToShow[1].offsetTop -
        this.sliderItemsToShow[0].offsetTop;
      this.slidesPerPage = Math.floor(
        (this.slider.clientHeight - this.sliderItemsToShow[0].offsetTop) /
          this.sliderItemOffset,
      );
    }
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }
  disableControlButtons(button, disable = true) {
    if (button && disable) {
      button.setAttribute("disabled", "disabled");
    } else if (button && !disable) {
      button.removeAttribute("disabled");
    }
  }

  update() {
    const previousPage = this.currentPage;
    if (this.direction === "horizontal") {
      this.currentPage =
        Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
    } else {
      this.currentPage = Math.ceil(
        this.slider.scrollTop / (this.sliderItemOffset * this.slidersScroll),
      );
    }
    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }
    if (this.currentPage != previousPage) {
      if (this.carouselTrackingType) {
        trackCarouselSwiped({
          carousel_type: this.carouselTrackingType,
          carousel_index: this.currentPage,
        });
      }
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        }),
      );
    }
    let scroll;
    if (this.direction === "horizontal") {
      scroll = this.slider.scrollLeft;
    } else {
      scroll = this.slider.scrollTop;
    }
    if (scroll === 0) {
      this.disableControlButtons(this.prevButton);
      if (this.slidePrevCounter) {
        this.slidePrevCounter.style.display = "none";
      }
    } else {
      this.disableControlButtons(this.prevButton, false);
      if (this.slidePrevCounter) {
        this.slidePrevCounter.style.display = "block";
      }
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
      )
    ) {
      !this.loopBackToStart && this.disableControlButtons(this.nextButton);
      if (this.slideNextCounter) {
        this.slideNextCounter.style.display = "none";
      }
    } else {
      this.disableControlButtons(this.nextButton, false);
      if (this.slideNextCounter) {
        this.slideNextCounter.style.display = "block";
      }
    }
  }

  isSlideVisible(element = null, offset = Number(this.offset)) {
    if (!element) return false;
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return this.direction === "horizontal"
      ? element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
          element.offsetLeft >= this.slider.scrollLeft
      : element.offsetTop + element.clientHeight <= lastVisibleSlide &&
          element.offsetTop >= this.slider.scrollTop;
  }

  slide(direction = "next", step = 1) {
    if (this.direction === "horizontal") {
      this.slideScrollPosition =
        direction === "next"
          ? this.slider.scrollLeft + step * this.sliderItemOffset
          : this.slider.scrollLeft - step * this.sliderItemOffset;
      if (
        this.isSlideVisible(
          this.sliderItemsToShow[this.sliderItemsToShow.length - 1],
        )
      )
        this.slideScrollPosition = -1 * this.slideScrollPosition;
      this.slider.scrollTo({
        left: this.slideScrollPosition,
      });
    } else {
      const direction = event.currentTarget.name === "next" ? 1 : -1;
      const offset = direction * step * this.sliderItemOffset;
      this.slideScrollPosition = this.slider.scrollTop + offset;
      this.slider.scrollTo({
        top: this.slideScrollPosition,
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slide(event.currentTarget.name, step);
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.carouselTrackingType = this.dataset.trackingType;
  }
  connectedCallback() {
    if (document.readyState === "complete") {
      this.initializeSlider();
    } else {
      window.addEventListener("load", () => {
        this.initializeSlider();
      });
    }
  }
  initializeSlider() {
    super.connectedCallback();
    if (this.dataset.trackingType !== "pdp-carousel") {
      this.sliderControlWrapper = Array.from(
        this.querySelectorAll(".slider-buttons"),
      ).find((el) => el.closest(this.tagName.toLowerCase()) === this);
    } else {
      this.sliderControlWrapper = this.querySelector(".slider-buttons");
    }
    this.scrolling = null;
    this.sliderFirstItemNode = this.sliderItemsToShow[0];
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.sliderControlLinksArray = this.sliderControlWrapper
      ? Array.from(
          this.sliderControlWrapper.querySelectorAll(".slider-counter__link"),
        )
      : [];
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this)),
    );
    this.slider.addEventListener(
      "scroll",
      this.handleScroll.bind(this),
      supportsPassive ? { passive: true } : false,
    );
    this.setSlideVisibility();

    if (
      this.slider.getAttribute("data-autoplay") === "true" &&
      this.sliderFirstItemNode
    )
      this.setAutoPlay();
  }
  handleScroll() {
    clearTimeout(this.scrolling);
    this.scrolling = setTimeout(this.setSlideVisibility.bind(this), 100);
  }
  setAutoPlay() {
    this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.defaultSpeed = this.autoplaySpeed;
    if (this.sliderAutoplayButton) {
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this),
      );
    }

    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    this.play();
    this.autoplayButtonIsSetToPlay = true;
  }
  onButtonClick(event) {
    super.onButtonClick(event);
    const isFirstSlide = this.currentPage === 1;
    const isLastSlide =
      this.currentPage === this.sliderItemsToShow.length - this.visibleSlides;
    if ((!isFirstSlide && !isLastSlide) || this.dataset.finite) return;

    if (isFirstSlide && event.currentTarget.name === "previous") {
      if (this.direction === "horizontal") {
        this.slideScrollPosition =
          this.slider.scrollLeft +
          this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
      } else {
        this.slideScrollPosition =
          this.slider.scrollTop +
          this.sliderFirstItemNode.clientHeight * this.sliderItemsToShow.length;
      }
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }
    if (this.direction === "horizontal") {
      this.slider.scrollTo({
        left: this.slideScrollPosition,
      });
    } else {
      this.slider.scrollTo({
        top: this.slideScrollPosition,
      });
    }
  }

  update() {
    super.update();
    if (this.dataset.trackingType !== "pdp-carousel") {
      this.sliderControlButtons = Array.from(
        this.querySelectorAll(".slider-counter__link"),
      ).filter((el) => el.closest(this.tagName.toLowerCase()) === this);
    } else {
      this.sliderControlButtons = this.querySelectorAll(
        ".slider-counter__link",
      );
    }

    if (!this.sliderControlButtons.length) return;
    const currentSliderControl =
      this.sliderControlButtons[this.currentPage - 1];
    if (!currentSliderControl) return;
    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    if (currentSliderControl) {
      currentSliderControl.classList.add("slider-counter__link--active");
      currentSliderControl.setAttribute("aria-current", true);
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling() {
    if (!this.autoplayButtonIsSetToPlay) return;
    this.play();
  }

  focusInHandling() {
    if (this.autoplayButtonIsSetToPlay) {
      this.pause();
    } else {
      this.play();
    }
  }

  play() {
    clearInterval(this.autoplay);
    const activeSlide = this.querySelector('[aria-hidden="false"]');
    this.autoplaySpeed = this.defaultSpeed;
    if (activeSlide && activeSlide.getAttribute("data-speed")) {
      this.autoplaySpeed = activeSlide.dataset.speed * 1000;
    }

    this.slider.setAttribute("aria-live", "off");
    setTimeout(() => {
      this.autoRotateSlides();
    }, this.autoplaySpeed);
  }
  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (!sliderAutoplayButton) return;

    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow,
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow,
      );
    }
  }

  autoRotateSlides() {
    let slideScrollPosition = 0;
    if (
      this.currentPage !== this.sliderItems.length &&
      this.sliderFirstItemNode
    ) {
      if (this.direction === "horizontal") {
        slideScrollPosition =
          this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth;
      } else {
        slideScrollPosition =
          this.slider.scrollTop + this.sliderFirstItemNode.clientHeight;
      }
    }
    const activeSlideNumber =
      this.currentPage === this.sliderItems.length ? 0 : this.currentPage;
    const activeSlide = this.sliderItems[activeSlideNumber];
    this.autoplaySpeed = this.defaultSpeed;
    if (activeSlide && activeSlide.getAttribute("data-speed")) {
      this.autoplaySpeed = activeSlide.dataset.speed * 1000;
    }
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }

  setSlideVisibility() {
    this.sliderItemsToShow.forEach((item, index) => {
      const links = item.querySelectorAll("a");
      const buttons = item.querySelectorAll("button");
      if (index === this.currentPage - 1) {
        if (buttons.length)
          buttons.forEach((button) => button.removeAttribute("tabindex"));
        if (links.length)
          links.forEach((link) => link.removeAttribute("tabindex"));
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
        this.autoplaySpeed = item.dataset.speed * 1000;
      } else {
        if (buttons.length && this.itemsInView <= index)
          buttons.forEach((button) => button.setAttribute("tabindex", "-1"));
        if (links.length)
          links.forEach((link) => link.setAttribute("tabindex", "-1"));
        if (item.tagName.toLowerCase() !== "a" && this.itemsInView <= index) {
          item.setAttribute("aria-hidden", "true");
          item.setAttribute("tabindex", "-1");
        }
      }
    });
    if (this.autoplaySpeed) {
      clearInterval(this.autoplay);
      this.autoplay = setInterval(
        this.autoRotateSlides.bind(this),
        this.autoplaySpeed,
      );
    }
  }

  linkToSlide(event, selectedIndex = null) {
    event.preventDefault();
    let index = this.sliderControlLinksArray.indexOf(event.currentTarget);
    if (selectedIndex) {
      index = selectedIndex;
    }
    this.sliderFirstItemNode =
      this.sliderFirstItemNode || this.sliderItemsToShow[0];
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth * (index + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

const fetchDrawer = (hideLoading = false) => {
  return new Promise(async (resolve) => {
    const resText = await apiFetchText(
      "",
      `?section_id=cart-drawer${featureFlag(
        "CUSTOM_BUNDLE_CART_SUFFIX",
        "-disable-byob",
      )}`,
    );
    const cartDrawerHtml = new DOMParser()
      .parseFromString(resText, "text/html")
      .getElementById("cart-drawer");
    await loadScript(window.scripts.cartJs);
    if (!window.libraries["product-form"]) {
      await loadScript(window.scripts.productForm);
      window.libraries["product-form"] = "loaded";
    }
    await loadScript(window.scripts.cartDrawerJs);
    await loadScript(window.scripts.quantitySelector);
    if (!document.body.querySelector(`[src="${window.scripts.modalScript}"]`))
      await loadScript(window.scripts.modalScript);
    await loadScript(window.scripts.pMallCheckout);
    if (hideLoading) {
      cartDrawerHtml.classList.add("active");
    }
    const cartDrawerLoaderHtml = document.querySelector("#cart-drawer-loader");
    let clearItems = [];
    try {
      clearItems = JSON.parse(cartDrawerLoaderHtml.dataset.clearKeys);
    } catch (e) {}
    if (cartDrawerLoaderHtml) {
      if (clearItems.length) {
        const updates = {};
        clearItems.forEach((item) => (updates[item] = 0));
        const clearBody = {
          headers: {
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify({ updates }),
        };
        await apiFetch("", `${routes.cart_update_url}.js`, {
          ...clearBody,
        });
      }
      cartDrawerLoaderHtml.replaceWith(cartDrawerHtml);
    }
    const cardDrawer = document.getElementById("cart-drawer");
    resolve(cardDrawer);
  });
};
let cartDrawerLoader;

function triggerAlternativeCartCopyExperiment() {
  if (document.querySelector("[data-alternative-cart-copy]")) {
    triggerExperiment("EXP_20240207_Cart_Renewal_Copy");
  }
}
function toggleDrawer() {
  if (!cartDrawerLoader)
    cartDrawerLoader = document.getElementById("cart-drawer-loader");
  cartDrawerLoader.classList.toggle("animate");
  cartDrawerLoader.classList.toggle("active");
  document.body.classList.toggle("overflow-hidden");
}

const openDrawer = async () => {
  if (!window.cartDrawer) {
    toggleDrawer();
    window.cartDrawer = await fetchDrawer(true);
    trackCartOpened && trackCartOpened();
    return;
  }
  trackCartOpened && trackCartOpened();
  window.cartDrawer.open();
};

function appendCouponToCookie(coupon) {
  if (!coupon) {
    return;
  }
  const existingCouponsInCookie = getCookie("override_coupons");
  if (!existingCouponsInCookie) {
    setCookie("override_coupons", coupon);
    return;
  }
  const uniqueCoupons = new Set(existingCouponsInCookie.split(","));
  uniqueCoupons.add(coupon.toUpperCase());
  setCookie("override_coupons", Array.from(uniqueCoupons).toString(), 1);
}
/**
 * Open Cookies Settings Banner
 */
const openCookieBanner = () => {
  const pandectesButton = document.querySelector(
    ".pd-cookie-banner-window .cc-settings",
  );
  if (pandectesButton) {
    pandectesButton.click();
  }
};

function getUserTraits() {
  try {
    window.dataLayer = window.dataLayer || [];
    const dataLayerReverse = window.dataLayer.concat([]).reverse();
    const userTraitArray = dataLayerReverse.find(
      (item) => item.event === "identifiedUserTraits",
    ) || { traits: {} };
    return userTraitArray.traits;
  } catch (error) {
    return {};
  }
}

function getCapiData() {
  const data = {
    fbp: null,
    fbc: null,
    userAgent: null,
  };
  try {
    data.fbp = getCookie("_fbp") || null;
    data.fbc = getCookie("_fbc") || null;
    data.ttclid = getCookie("ttclid") || null;
    data.ttp = getCookie("_ttp") || null;
    data.userAgent = window.navigator.userAgent || null;
  } catch (e) {
    logger.error(e);
  }
  return data;
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone; // e.g Europe/Paris
  } catch (error) {
    return "";
  }
}

function cleanObject(data = {}) {
  return Object.entries(data).reduce(
    (a, [k, v]) => (v ? ((a[k] = v), a) : a),
    {},
  );
}

/**
 * Maps property values to order categories.
 *
 * @param {string} property - The property value to map.
 * @returns {string} The corresponding order category.
 */

function getOrderCategoryFromProperty(property) {
  const propertyToCategoryMap = {
    full_size_special_offer: "FullSizeSpecialOffer",
    free_trial: "FreeTrial",
    "subscription-program": "SubscriptionProgram",
    custom_bundle: "CustomBundle",
    "subscription-program-50": "SubscriptionProgram",
  };

  return propertyToCategoryMap[property] || "otp";
}

/**
 * Determines the order categories for the given cart items from either a checkout or custom storefront.
 *
 * @param {Object[]} cartItems - The cart items to process.
 * @returns {string[]} An array of unique order categories.
 */

function getOrderCategory(cartItems) {
  const itemTypes = cartItems.map((item) => {
    let currentItemType = "otp";

    if (item.selling_plan) {
      currentItemType = "Subscription";
    }

    // Parse GraphQL custom properties items
    const keyAttribute = item?.attributes?.find(
      (attr) => attr.key === "_item_type",
    );
    if (keyAttribute) {
      currentItemType = getOrderCategoryFromProperty(keyAttribute.value);
    }

    // Parse Shopify cart items
    const typeProperty = item.properties?._item_type;
    if (typeProperty) {
      currentItemType = getOrderCategoryFromProperty(typeProperty);
    }

    return currentItemType;
  });

  return [...new Set(itemTypes)];
}

function getCartUserAttributes(orderType = "otp", cartItems = []) {
  const cartAttributes = {};
  const userTraits = getUserTraits();
  try {
    const capiData = getCapiData();
    cartAttributes.user_traits = JSON.stringify(userTraits);
    cartAttributes.fbId = capiData.fbp;
    cartAttributes.fbc = capiData.fbc;
    cartAttributes.ttclid = capiData.ttclid;
    cartAttributes.ttp = capiData.ttp;
    cartAttributes.shippingProcessingTime = theme.shop.shipping_processing_time;
    cartAttributes.user_agent = navigator.userAgent;
    cartAttributes.deviceId = getDeviceId();
    cartAttributes["order-type"] = orderType;
    cartAttributes["initial-order-type"] = orderType;
    cartAttributes.orderCategory = getOrderCategory(cartItems).join(",");
    cartAttributes.exchangeRate = theme.cart.currency_with_vat;
    cartAttributes.timezone = getTimezone();
    cartAttributes.clientId = theme.analytics.clientId;
    cartAttributes.isFullSizeCustomer = String(
      userTraits.initial_page_group !== "free_trial",
    );
  } catch (error) {
    logger.error(`Cart Attribue Error: ${error}`);
  }
  return cleanObject(cartAttributes);
}
function isObjectValuesEmpty(obj = {}) {
  return Object.values(obj).every((value) => value === "");
}

function makeButtonAccessible(element, handler) {
  if (!element) return;
  element.addEventListener("click", handler);
  element.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handler.call(element, e);
    }
  });
}

/**
 * Tracks when specified sections of the page become at least 50% visible in the viewport.
 * Useful for engagement analytics, funnel tracking, and behavioral insights.
 *
 * How It Works:
 * - Uses IntersectionObserver with a 50% visibility threshold.
 * - When a matched element is at least 50% visible:
 *     - Pushes a `scrolled_into_section` event to the data layer via `layerPush()`.
 *     - Includes `scrolledIntoSectionData` for GTM and other analytics tools.
 *     - Stops observing the element after the first trigger to avoid duplicate events.
 *
 * Usage:
 * - Call `theme.registerSection({ cssSelector, sectionTitle })` for any section you want to track.
 *   Example:
 *     theme.registerSection({ cssSelector: '.hero-banner', sectionTitle: 'hero_banner' });
 *
 * Notes:
 * - Automatically initializes the observer on DOMContentLoaded.
 * - Each element is tracked only once.
 */

document.addEventListener("DOMContentLoaded", function () {
  // Global store of registered sections
  let sectionObservable = null;

  // Observer options
  const observerOptions = {
    root: null, // viewport
    rootMargin: "0px",
    threshold: 0.5, // 50% visibility triggers
  };

  // Keep track of already observed elements
  const observedElements = new WeakSet();

  // Observer callback logic
  const observerCallback = (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const target = entry.target;
        const title = target.sectionTitle;

        if (!observedElements.has(target)) {
          trackSectionView(title);

          observedElements.add(target);
          if (sectionObservable) {
            sectionObservable.unobserve(target);
          }
        }
      }
    });
  };

  // Initialize observer for all registered selectors
  const initObserver = () => {
    if (!sectionObservable) {
      sectionObservable = new IntersectionObserver(
        observerCallback,
        observerOptions,
      );
    }
  };

  const trackSectionView = (scrolledIntoSectionData) => {
    return layerPush({
      event: "scrolled_into_section",
      scrolledIntoSectionData,
    });
  };

  // Utility: Register a new section selector to observe
  theme.registerSection = ({ cssSelector, sectionTitle }) => {
    initObserver();

    const elements = document.querySelectorAll(cssSelector);

    elements.forEach((element) => {
      element.sectionTitle = sectionTitle;
      if (!observedElements.has(element)) {
        sectionObservable.observe(element);
      }
    });
  };
});

class CartUpsell extends HTMLElement {
  constructor() {
    super();
    this.trackedData = new Set();
  }

  connectedCallback() {
    this.sliderComponent = this.querySelector(".cart-upsell__carousel");
    if (this.sliderComponent) {
      this.sliderComponent.addEventListener("slideChanged", (event) => {
        this.trackCartUpsellShown(event?.detail?.currentElement);
      });
    } else {
      // setTimout is used to wait for user traits to finish firing
      window.setTimeout(() => {
        this.trackCartUpsellShown(this);
      }, 200);
    }
  }

  trackCartUpsellShown(currentElement) {
    try {
      const rawData = currentElement
        ?.querySelector('[data-query="upsell-data"]')
        .content.textContent.trim();

      if (rawData && !this.trackedData.has(rawData)) {
        const upsellEventData = JSON.parse(rawData);
        layerPush({
          event: "upsellShown",
          upsellEventData: upsellEventData,
        });
        this.trackedData.add(rawData);
      }
    } catch (error) {
      logger.error(error);
    }
  }
}
customElements.define("cart-upsell", CartUpsell);

class VideoPlayButton extends HTMLElement {
  constructor() {
    super();
  }

  init = () => {
    this.removeAttribute("aria-hidden");
    this.classList.remove("hide");
    this.video.tabIndex = -1;
  };

  handleClick = async () => {
    /** @type {"picture_in_picture" | "inline"} */
    const playMode = this.getAttribute("data-play-mode");

    this.remove();
    this.video.muted = false;
    this.video.controls = true;
    this.video.removeAttribute("tabindex");
    await this.video.play();

    if (playMode === "picture_in_picture") {
      this.video.requestPictureInPicture();
    }
  };

  connectedCallback() {
    const videoId = this.getAttribute("data-target");

    /** @type {HTMLButtonElement | null} */
    this.button = this.querySelector("button[data-action='playVideo']");

    /** @type {HTMLVideoElement | null} */
    this.video = document.getElementById(videoId);

    if (!(this.button && this.video)) {
      return;
    }
    this.button.addEventListener("click", this.handleClick);
    this.init();
  }
  disconnectedCallback() {
    this.button?.removeEventListener("click", this.handleClick);
  }
}

customElements.define("video-play-button", VideoPlayButton);

class SubmitButton extends HTMLElement {
  constructor() {
    super();
  }
  handleSubmit = () => {
    this.form?.classList.add("form-submitting");
  };
  connectedCallback() {
    this.form = this.closest("form");
    this.form?.addEventListener("submit", this.handleSubmit);
  }
  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.handleSubmit);
  }
}
customElements.define("submit-button", SubmitButton);

class CookieSettingsButton extends HTMLElement {
  constructor() {
    super();
    if (window.Pandectes?.hasInitialized) {
      this.init();
      return;
    }
    window.addEventListener("PandectesEvent_OnLoad", this.init);
  }
  init = () => {
    if (window.Pandectes?.banner?.isApplicable) {
      this.classList.remove("d-none");
    }
  };
}
customElements.define("cookie-settings-button", CookieSettingsButton);
